import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { salvarFoto } from "../../lib/foto";
import { criarExperiencia } from "../queries";
import { ExperienciaFoto } from "../ui/ExperienciaFoto";
import { GravadorAudio } from "../ui/GravadorAudio";
import {
  ANDAMENTO_LABEL,
  FAMILIARIDADE_LABEL,
  FUNCAO_LABEL,
  INTENSIDADE_LABEL,
  MODALIDADES_ORDEM,
  MODALIDADE_LABEL,
  PREVISIBILIDADE_LABEL,
  STATUS_EXPERIENCIA_LABEL,
  TIPOS_ORDEM,
  TIPO_LABEL,
  type Andamento,
  type Familiaridade,
  type FuncaoMusical,
  type Intensidade,
  type Modalidade,
  type Previsibilidade,
  type StatusExperiencia,
  type TipoExperiencia,
} from "../types";

/**
 * P0.1 — cadastro rico (feito 1×) de uma experiência musical. Só o nome é
 * obrigatório; o resto enriquece a leitura clínica e os derivados (parecidos/insights).
 */
const STATUS_OPCOES: StatusExperiencia[] = [
  "nao_usada",
  "em_exploracao",
  "favorita",
  "sensivel",
];

export function NovaExperiencia() {
  const nav = useNavigate();
  const [nome, setNome] = useState("");
  const [tipo, setTipo] = useState<TipoExperiencia>("cancao");
  const [funcao, setFuncao] = useState<FuncaoMusical | "">("");
  const [modalidade, setModalidade] = useState<Modalidade[]>([]);
  const [andamento, setAndamento] = useState<Andamento | "">("");
  const [intensidade, setIntensidade] = useState<Intensidade | "">("");
  const [timbre, setTimbre] = useState("");
  const [previsibilidade, setPrevisibilidade] = useState<Previsibilidade | "">("");
  const [familiaridade, setFamiliaridade] = useState<Familiaridade | "">("");
  const [status, setStatus] = useState<StatusExperiencia>("nao_usada");
  const [observacoes, setObservacoes] = useState("");
  const [midias, setMidias] = useState<string[]>([]);
  const [mostrarGravador, setMostrarGravador] = useState(false);
  const [salvandoMidia, setSalvandoMidia] = useState(false);

  function toggleModalidade(m: Modalidade) {
    setModalidade((cur) =>
      cur.includes(m) ? cur.filter((x) => x !== m) : [...cur, m],
    );
  }

  async function anexarBlob(blob: Blob) {
    setSalvandoMidia(true);
    try {
      const ref = await salvarFoto(blob);
      setMidias((cur) => [...cur, ref]);
      setMostrarGravador(false);
    } finally {
      setSalvandoMidia(false);
    }
  }

  async function salvar() {
    if (!nome.trim()) return;
    const id = await criarExperiencia({
      nome,
      tipo,
      funcao: funcao || null,
      modalidade,
      sonoro: {
        andamento: andamento || null,
        intensidade: intensidade || null,
        timbre: timbre.trim() || null,
        previsibilidade: previsibilidade || null,
        familiaridade: familiaridade || null,
      },
      status,
      observacoes: observacoes.trim() || null,
      midias,
    });
    nav(`/compasso/experiencia/${id}`, { replace: true });
  }

  return (
    <main className="tela">
      <button className="btn btn-fantasma" onClick={() => nav(-1)}>
        ← voltar
      </button>
      <h1 className="tela-titulo">nova experiência</h1>
      <p className="tela-sub">só o nome é obrigatório. o resto você completa quando quiser.</p>

      <div className="card">
        <div className="campo">
          <label>nome</label>
          <input
            type="text"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            placeholder='ex.: "tambor azul", "canção do oi"'
            autoFocus
          />
        </div>

        <div className="campo">
          <label>tipo</label>
          <select value={tipo} onChange={(e) => setTipo(e.target.value as TipoExperiencia)}>
            {TIPOS_ORDEM.map((t) => (
              <option key={t} value={t}>
                {TIPO_LABEL[t]}
              </option>
            ))}
          </select>
        </div>

        <div className="campo">
          <label>função pretendida <span style={{ color: "var(--text-suave)" }}>(opcional)</span></label>
          <select value={funcao} onChange={(e) => setFuncao(e.target.value as FuncaoMusical | "")}>
            <option value="">—</option>
            {(Object.keys(FUNCAO_LABEL) as FuncaoMusical[]).map((f) => (
              <option key={f} value={f}>
                {FUNCAO_LABEL[f]}
              </option>
            ))}
          </select>
        </div>

        <div className="campo" style={{ margin: 0 }}>
          <label>como a criança participa</label>
          <div className="status-opcoes">
            {MODALIDADES_ORDEM.map((m) => (
              <button
                key={m}
                type="button"
                className={`status-opt${modalidade.includes(m) ? " sel" : ""}`}
                aria-pressed={modalidade.includes(m)}
                onClick={() => toggleModalidade(m)}
              >
                {MODALIDADE_LABEL[m]}
              </button>
            ))}
          </div>
        </div>
      </div>

      <section className="secao" style={{ marginTop: "var(--s4)" }}>
        <h2 className="secao-titulo">
          mídia <span style={{ color: "var(--text-suave)" }}>(opcional)</span>
        </h2>
        <div className="card">
          <div className="row" style={{ marginBottom: "var(--s2)" }}>
            <label className="foto-editavel" title="adicionar foto">
              <ExperienciaFoto midias={midias} nome={nome} tipo={tipo} />
              <span className="foto-editavel-cam" aria-hidden>
                📷
              </span>
              <input
                type="file"
                accept="image/*"
                capture="environment"
                hidden
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void anexarBlob(f);
                  e.target.value = "";
                }}
              />
            </label>
            <div className="crescer">
              <div className="meta" style={{ color: "var(--text-muted)" }}>
                {midias.length === 0
                  ? "uma foto ajuda o reconhecimento; um clipe de áudio toca no modo escolha."
                  : `${midias.length} ${midias.length === 1 ? "mídia anexada" : "mídias anexadas"}`}
              </div>
            </div>
          </div>

          <div className="row" style={{ gap: "var(--s2)" }}>
            <button
              type="button"
              className="btn btn-fantasma crescer"
              aria-pressed={mostrarGravador}
              onClick={() => setMostrarGravador((v) => !v)}
            >
              🎙️ {mostrarGravador ? "fechar" : "gravar"}
            </button>
            <label className="btn btn-fantasma crescer" style={{ cursor: "pointer" }}>
              📎 anexar áudio
              <input
                type="file"
                accept="audio/*"
                hidden
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void anexarBlob(f);
                  e.target.value = "";
                }}
              />
            </label>
          </div>

          {mostrarGravador && (
            <div style={{ marginTop: "var(--s3)" }}>
              <GravadorAudio onSalvar={anexarBlob} salvando={salvandoMidia} />
            </div>
          )}
        </div>
      </section>

      <section className="secao" style={{ marginTop: "var(--s4)" }}>
        <h2 className="secao-titulo">perfil sonoro (opcional)</h2>
        <div className="card">
          <div className="linha-campos">
            <div className="campo">
              <label>andamento</label>
              <select value={andamento} onChange={(e) => setAndamento(e.target.value as Andamento | "")}>
                <option value="">—</option>
                {(Object.keys(ANDAMENTO_LABEL) as Andamento[]).map((a) => (
                  <option key={a} value={a}>{ANDAMENTO_LABEL[a]}</option>
                ))}
              </select>
            </div>
            <div className="campo">
              <label>intensidade</label>
              <select value={intensidade} onChange={(e) => setIntensidade(e.target.value as Intensidade | "")}>
                <option value="">—</option>
                {(Object.keys(INTENSIDADE_LABEL) as Intensidade[]).map((i) => (
                  <option key={i} value={i}>{INTENSIDADE_LABEL[i]}</option>
                ))}
              </select>
            </div>
            <div className="campo">
              <label>previsibilidade</label>
              <select
                value={previsibilidade}
                onChange={(e) => setPrevisibilidade(e.target.value as Previsibilidade | "")}
              >
                <option value="">—</option>
                {(Object.keys(PREVISIBILIDADE_LABEL) as Previsibilidade[]).map((p) => (
                  <option key={p} value={p}>{PREVISIBILIDADE_LABEL[p]}</option>
                ))}
              </select>
            </div>
            <div className="campo">
              <label>familiaridade</label>
              <select
                value={familiaridade}
                onChange={(e) => setFamiliaridade(e.target.value as Familiaridade | "")}
              >
                <option value="">—</option>
                {(Object.keys(FAMILIARIDADE_LABEL) as Familiaridade[]).map((f) => (
                  <option key={f} value={f}>{FAMILIARIDADE_LABEL[f]}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="campo" style={{ margin: 0 }}>
            <label>timbre <span style={{ color: "var(--text-suave)" }}>(livre)</span></label>
            <input
              type="text"
              value={timbre}
              onChange={(e) => setTimbre(e.target.value)}
              placeholder='ex.: "metálico", "voz", "madeira"'
            />
          </div>
        </div>
      </section>

      <section className="secao">
        <h2 className="secao-titulo">situação</h2>
        <div className="card">
          <div className="status-opcoes">
            {STATUS_OPCOES.map((s) => (
              <button
                key={s}
                type="button"
                className={`status-opt${status === s ? " sel" : ""}`}
                aria-pressed={status === s}
                onClick={() => setStatus(s)}
              >
                {STATUS_EXPERIENCIA_LABEL[s]}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="secao">
        <h2 className="secao-titulo">observações (opcional)</h2>
        <div className="card">
          <div className="campo" style={{ margin: 0 }}>
            <textarea
              value={observacoes}
              onChange={(e) => setObservacoes(e.target.value)}
              placeholder="ex.: 'só com volume baixo', 'acalma na transição da escola'"
            />
          </div>
        </div>
      </section>

      <button
        className="btn btn-primario btn-bloco btn-grande"
        disabled={!nome.trim()}
        onClick={salvar}
      >
        salvar experiência
      </button>
    </main>
  );
}
